/** US federal holidays and company calendar helpers (demo). */

export type HolidaySource = "federal" | "company";

export type CompanyHoliday = {
  id: string;
  date: string; // YYYY-MM-DD
  name: string;
  source: HolidaySource;
};

export type HolidayCalendarMode = "inherit_federal" | "start_empty";

/** Observed US federal holidays for a given year (weekday observance). */
export function federalHolidaysForYear(year: number): Omit<CompanyHoliday, "id">[] {
  const nthWeekday = (y: number, month: number, weekday: number, n: number) => {
    // month 0-based; weekday 0=Sun
    let count = 0;
    for (let d = 1; d <= 31; d++) {
      const dt = new Date(Date.UTC(y, month, d));
      if (dt.getUTCMonth() !== month) break;
      if (dt.getUTCDay() === weekday) {
        count++;
        if (count === n) return dt.toISOString().slice(0, 10);
      }
    }
    return `${y}-${String(month + 1).padStart(2, "0")}-01`;
  };
  const lastWeekday = (y: number, month: number, weekday: number) => {
    for (let d = 31; d >= 1; d--) {
      const dt = new Date(Date.UTC(y, month, d));
      if (dt.getUTCMonth() !== month) continue;
      if (dt.getUTCDay() === weekday) return dt.toISOString().slice(0, 10);
    }
    return `${y}-${String(month + 1).padStart(2, "0")}-01`;
  };
  const observe = (y: number, month: number, day: number) => {
    const dt = new Date(Date.UTC(y, month - 1, day));
    const wd = dt.getUTCDay();
    if (wd === 0) dt.setUTCDate(dt.getUTCDate() + 1); // Sun → Mon
    if (wd === 6) dt.setUTCDate(dt.getUTCDate() - 1); // Sat → Fri
    return dt.toISOString().slice(0, 10);
  };

  return [
    { date: observe(year, 1, 1), name: "New Year's Day", source: "federal" },
    { date: nthWeekday(year, 0, 1, 3), name: "Birthday of Martin Luther King, Jr.", source: "federal" },
    { date: nthWeekday(year, 1, 1, 3), name: "Washington's Birthday", source: "federal" },
    { date: lastWeekday(year, 4, 1), name: "Memorial Day", source: "federal" },
    { date: observe(year, 6, 19), name: "Juneteenth National Independence Day", source: "federal" },
    { date: observe(year, 7, 4), name: "Independence Day", source: "federal" },
    { date: nthWeekday(year, 8, 1, 1), name: "Labor Day", source: "federal" },
    { date: nthWeekday(year, 9, 1, 2), name: "Columbus Day", source: "federal" },
    { date: observe(year, 11, 11), name: "Veterans Day", source: "federal" },
    { date: nthWeekday(year, 10, 4, 4), name: "Thanksgiving Day", source: "federal" },
    { date: observe(year, 12, 25), name: "Christmas Day", source: "federal" },
  ];
}

export function seedFederalHolidays(years: number[]): CompanyHoliday[] {
  const out: CompanyHoliday[] = [];
  for (const y of years) {
    for (const h of federalHolidaysForYear(y)) {
      out.push({ ...h, id: `fed-${h.date}-${h.name.slice(0, 12)}` });
    }
  }
  return out.sort((a, b) => a.date.localeCompare(b.date));
}

export function mergeFederal(existing: CompanyHoliday[], years: number[]): CompanyHoliday[] {
  const have = new Set(existing.map((h) => h.date));
  const merged = [...existing];
  for (const h of seedFederalHolidays(years)) {
    if (!have.has(h.date)) {
      merged.push(h);
      have.add(h.date);
    }
  }
  return merged.sort((a, b) => a.date.localeCompare(b.date));
}
